import React from 'react';

interface Slot {
  time: string;
  name: string;
  service?: string;
  barber?: string;
  state: 'done' | 'now' | 'next' | 'gap';
}

interface Kpi {
  value: string;
  label: string;
}

interface AgendaPreviewProps {
  /** Texto do cabecalho mono (ex: "agenda · hoje · ter, 17 mai"). */
  headerLabel?: string;
  slots?: Slot[];
  kpis?: Kpi[];
}

const DEFAULT_SLOTS: Slot[] = [
  { time: '09:00', name: 'Lucas R.', service: 'Corte + barba', barber: 'João', state: 'done' },
  { time: '10:30', name: 'Pedro M.', service: 'Corte fade', barber: 'João', state: 'now' },
  { time: '11:00', name: 'André T.', service: 'Barba', barber: 'Carlos', state: 'next' },
  { time: '11:30', name: '- vago -', state: 'gap' },
  { time: '12:00', name: 'Rafael S.', service: 'Corte social', barber: 'Carlos', state: 'next' },
];

const DEFAULT_KPIS: Kpi[] = [
  { value: '12', label: 'agendados hoje' },
  { value: '87%', label: 'taxa de ocupação' },
  { value: '4', label: 'no-show evitado' },
];

const DEFAULT_HEADER = 'agenda · hoje · ter, 17 mai';

function getInitials(name: string): string {
  const cleaned = name.replace(/[^A-Za-zÀ-ÿ\s]/g, '').trim();
  if (!cleaned) return '·';
  return cleaned
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function StatusBadge({ state }: { state: Slot['state'] }) {
  if (state === 'now') return <span className="badge badge--warning">Agora</span>;
  if (state === 'done') return <span className="badge badge--success">Concluído</span>;
  if (state === 'next') return <span className="badge badge--info">Próximo</span>;
  return <span className="badge badge--neutral">Vago</span>;
}

/**
 * Mock visual do produto exibido no painel esquerdo do login. "Mostra
 * o produto em vez de descrever" - 5 slots com 4 estados visuais e 3
 * KPIs no rodape. Tudo estatico (nao consulta API).
 *
 * Markup baseado na variacao "Tabelas com avatar, checkbox e densidade"
 * do design system (apps/web/app/admin/design-system/page.tsx). Usa
 * data-table--compact com avatar de iniciais e meta (servico + barbeiro)
 * - garante linhas demarcadas e densidade consistente em qualquer tema
 * (claro, escuro, cliente, sistema). O CSS dessa tabela vive em
 * apps/web/design-system/components/styles/data-table.css.
 *
 * So renderiza correto dentro de `<AuthShell>` (precisa dos tokens).
 */
export function AgendaPreview({
  headerLabel = DEFAULT_HEADER,
  slots = DEFAULT_SLOTS,
  kpis = DEFAULT_KPIS,
}: AgendaPreviewProps) {
  return (
    <div className="auth-agenda" aria-label="Pré-visualização da agenda">
      <div className="auth-agenda__head">
        <div className="auth-agenda__head-left">
          <span className="auth-agenda__live-dot" aria-hidden="true" />
          <span className="auth-agenda__head-label">{headerLabel}</span>
        </div>
        <div className="auth-agenda__head-dots" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
      </div>

      <div className="data-table-card auth-agenda__table-card">
        <div className="data-table-wrap">
          <table className="data-table data-table--compact">
            <thead>
              <tr>
                <th>Horário</th>
                <th>Cliente</th>
                <th className="data-table__cell--right">Status</th>
              </tr>
            </thead>
            <tbody>
              {slots.map((slot, i) => (
                <tr
                  key={`${slot.time}-${i}`}
                  className={slot.state === 'now' ? 'is-selected' : ''}
                >
                  <td className="data-table__num">{slot.time}</td>
                  <td>
                    <div className="data-table__user">
                      <span className="data-table__user-avatar" aria-hidden="true">
                        {getInitials(slot.name)}
                      </span>
                      <div className="data-table__user-meta">
                        <div className="data-table__user-name">{slot.name}</div>
                        {slot.service && (
                          <div className="data-table__user-sub">
                            {slot.service}
                            {slot.barber ? ` · ${slot.barber}` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="data-table__cell--right">
                    <StatusBadge state={slot.state} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="auth-agenda__kpis">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="auth-agenda__kpi">
            <p className="auth-agenda__kpi-value">{kpi.value}</p>
            <p className="auth-agenda__kpi-label">{kpi.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
